<template>
    <div class="flex flex-col">
        <h3
            class="align-center mb-2 flex align-bottom text-lg font-bold text-gray-400"
        >
            <span>Score</span>
            <span
                v-if="updating"
                class="ml-2 flex animate-pulse items-center rounded bg-gray-600 px-2 align-middle text-xs text-white dark:bg-gray-200 dark:text-gray-800"
                :class="{
                    '': loading,
                }"
            >
                Atualizando
            </span>

            <div class="ml-auto flex items-center">
                <div v-if="canManageBypass" class="mr-4 flex items-center">
                    <span class="mr-2">Bypass</span>
                    <label class="relative inline-flex cursor-pointer items-center">
                        <input 
                            type="checkbox" 
                            class="peer sr-only" 
                            :checked="bypassActive"
                            @change="toggleBypass"
                            :disabled="loadingBypass"
                        >
                        <div class="h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all after:content-[''] peer-checked:bg-blue-600 peer-checked:after:translate-x-full peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:bg-gray-700 dark:peer-focus:ring-blue-800"></div>
                    </label>
                </div>
                <p v-if="score" class="mr-1">
                    Atualizado em: {{ score.created_at }}
                </p>
                <submit-button
                    size="sm"
                    :loading="loading"
                    :disabled="loading || updating"
                    @click="store"
                >
                    Calcular
                </submit-button>
            </div>
        </h3>

        <ticto-loading v-if="loading" />
        <template v-else>
            <div v-if="selectedScoreId" class="mb-4 rounded-lg bg-blue-50 border border-blue-200 p-3 dark:bg-blue-900/20 dark:border-blue-800">
                <div class="flex items-center justify-between">
                    <p class="text-sm text-blue-700 dark:text-blue-300">
                        <strong>Score histórico selecionado:</strong> {{ score.created_at }}
                    </p>
                    <button 
                        @click="resetToCurrentScore"
                        class="text-sm text-blue-600 hover:text-blue-800 underline dark:text-blue-400 dark:hover:text-blue-200"
                    >
                        Voltar ao score atual
                    </button>
                </div>
            </div>
            
            <div class="grid grid-cols-2 gap-6" v-if="score">
                <div class="flex flex-col space-y-4">
                    <score-gauge :score="score.amount"/>
                    <score-table :score="score" />
                </div>
                
                <score-evolution 
                    :user="user" 
                    @score-selected="onScoreSelected"
                />
            </div>
        </template>
    </div>
</template>

<script>
import SubmitButton from "../forms/SubmitButton.vue";
import TictoLoading from "../layouts/TictoLoading.vue";
import Card from "../layouts/Card.vue";
import ScoreGauge from "./score/ScoreGauge.vue";
import ScoreTable from "./score/ScoreTable.vue";
import ScoreEvolution from "./score/ScoreEvolution.vue";
import { Store } from "../../stores/store";
import { mapWritableState } from "pinia";

export default {
    props: ["user"],
    emits: ["data-loaded"],
    components: {
        ScoreTable,
        Card,
        TictoLoading,
        SubmitButton,
        ScoreGauge,
        ScoreEvolution,
    },
    data() {
        return {
            updating: false,
            loading: true,
            loadingBypass: false,
            seller: false,
            score: false,
            bypassActive: false,
            selectedScoreId: null,
        };
    },

    computed: {
        ...mapWritableState(Store, ["authUser"]),
        canManageBypass() {
            return this.authUser && this.authUser.id === 2;
        },
    },

    mounted() {
        this.fetch();
        this.checkBypassStatus();

        Echo.channel("user-score-" + this.user.id).listen(
            ".App\\Events\\Score\\UpdatingScore",
            (e) => {
                this.updating = e.updating;

                if (!e.updating) {
                    this.fetch();
                }
            },
        );
    },

    methods: {
        fetch() {
            this.loading = true;

            axios
                .get("/api/user/" + this.user.id + "/score")
                .then(({ data }) => {
                    this.seller = data.seller;
                    this.score = data.score;
                    this.loading = false;
                    this.emitScoreData();
                });
        },
        store() {
            this.loading = true;

            axios
                .post("/api/user/" + this.user.id + "/score")
                .then(({ data }) => {
                    this.loading = false;
                    this.fetch();
                });
        },
        checkBypassStatus() {
            if (!this.canManageBypass) return;
            
            axios
                .get(`/api/user/${this.user.id}/score/bypass`)
                .then(({ data }) => {
                    this.bypassActive = data.active;
                })
                .catch(error => {
                    console.error("Erro ao verificar status do bypass:", error);
                });
        },
        toggleBypass() {
            if (!this.canManageBypass) return;
            
            this.loadingBypass = true;
            
            axios
                .post(`/api/user/${this.user.id}/score/bypass/toggle`)
                .then(({ data }) => {
                    this.bypassActive = data.active;
                    this.emitScoreData(); // Atualiza o summary quando o bypass muda
                    this.$toasted.show(
                        data.active 
                            ? "Bypass de score ativado com sucesso!" 
                            : "Bypass de score desativado com sucesso!",
                        { type: "success" }
                    );
                })
                .catch(error => {
                    console.error("Erro ao alternar bypass:", error);
                    this.$toasted.show(
                        "Erro ao alternar bypass de score.",
                        { type: "error" }
                    );
                })
                .finally(() => {
                    this.loadingBypass = false;
                });
        },
        
        async onScoreSelected(scoreId) {
            this.selectedScoreId = scoreId;
            this.loading = true;
            
            try {
                const { data } = await axios.get(`/api/user/${this.user.id}/score/${scoreId}`);
                this.score = data.score;
                this.emitScoreData();
            } catch (error) {
                console.error("Erro ao buscar detalhes do score:", error);
                this.$toasted.show(
                    "Erro ao carregar detalhes do score selecionado.",
                    { type: "error" }
                );
            } finally {
                this.loading = false;
            }
        },
        
        resetToCurrentScore() {
            this.selectedScoreId = null;
            this.fetch();
        },
        
        emitScoreData() {
            if (this.score) {
                const scoreData = {
                    amount: this.score.amount,
                    level: this.getScoreLevel(this.score.amount),
                    bypass: this.bypassActive,
                    historical: this.selectedScoreId !== null,
                    updated_at: this.score.created_at
                };
                this.$emit('data-loaded', scoreData);
            }
        },
        
        getScoreLevel(score) {
            if (score >= 800) return 'excelente';
            if (score >= 600) return 'bom';
            if (score >= 400) return 'regular';
            if (score >= 200) return 'ruim';
            return 'muito ruim';
        },
    },
};
</script>
